import {
  type HttpEvent,
  type HttpInterceptorFn,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, switchMap, tap, throwError, from, type Observable } from 'rxjs';
import { SpinoffAuthService, classifySpinoffInfraError, type SpinoffInfraErrorKind } from './spinoff-auth.service';
import { SPINOFF_AUTH_CONFIG } from './spinoff-auth.config';
import { SPINOFF_LOCAL_ERROR_HANDLING } from './spinoff-auth-error-handling.config';

const INFRA_ERROR_TOAST_MESSAGE: Record<SpinoffInfraErrorKind, string> = {
  network: '서버에 연결할 수 없습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
  server: '일시적인 서비스 장애입니다. 잠시 후 다시 시도해 주세요.',
};

export const spinoffAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(SpinoffAuthService);
  const config = inject(SPINOFF_AUTH_CONFIG);
  // MatSnackBar 는 인프라 장애 토스트를 띄울 때만 필요해 정적 import/주입하지 않는다 — 정적으로 두면 snack-bar 모듈(약 14KB)이
  // 앱 초기 번들에 실린다. 필요한 시점에 `showInfraToast` 가 동적 import 하고, 이 injector 로 꺼낸다.
  const injector = inject(Injector);

  if (!req.url.startsWith(config.apiBaseUrl)) {
    return next(req);
  }

  const path = stripBaseUrl(req.url, config.apiBaseUrl);

  return dispatch(req, path, next, auth, config).pipe(
    tap((event) => {
      // 백엔드(fetch/XHR 어느 쪽이든)는 요청이 아직 완료되기 전에 `HttpEventType.Sent`부터
      // next()로 먼저 흘려보낸다(reportProgress 여부와 무관하게 항상). 이 tap이
      // HttpResponse 여부를 안 가리면, 응답 성공/실패가 갈리기도 전인 Sent 시점에
      // infraError가 매번 지워졌다가 실패하면 catchError가 다시 세우는 식으로 깜빡이거나,
      // 요청이 응답 없이 오래 멈춰 있는 동안 전체 화면 에러가 실제로는 회복 안 됐는데도
      // 사라져 보이는 오판정이 생긴다.
      if (event instanceof HttpResponse) {
        auth.clearInfraError();
      }
    }),
    catchError((err) => {
      // 401/404 등 정상적인 애플리케이션 실패와 네트워크 단절·DB 커넥션 풀 타임아웃·업스트림
      // 다운 같은 인프라 장애를 구분해, 후자만 앱 전체에 노출한다 — refresh뿐 아니라 이
      // 인터셉터를 지나는 모든 API 요청(org-scoped 포함)에서 관측한다. 단, 요청이
      // SPINOFF_LOCAL_ERROR_HANDLING으로 로컬 처리를 요청했으면(사용자가 이미 화면에 들어와
      // 직접 트리거한 액션 — 로그인 제출 등) 전역 화면 대신 토스트로만 알린다 — 그 액션을
      // 감싸는 화면 자체를 덮으면 사용자가 입력 중이던 폼 상태가 사라진다.
      const kind = classifySpinoffInfraError(err);
      if (kind) {
        if (req.context.get(SPINOFF_LOCAL_ERROR_HANDLING)) {
          showInfraToast(injector, INFRA_ERROR_TOAST_MESSAGE[kind]);
        } else {
          auth.reportInfraError(kind);
        }
      }
      return throwError(() => err);
    }),
  );
};

/**
 * 인프라 장애 토스트. 요청 오류를 던지는 흐름과 분리해(fire-and-forget) snack-bar 청크를 받는 동안 응답 오류 전달이 늦어지지 않게 하고,
 * 청크 로드 실패나 MatSnackBar 제공자 부재(Material 을 쓰지 않는 소비 앱·경량 테스트 환경)는 `console.error` 로 대체해 요청 오류를 가리지 않는다.
 */
function showInfraToast(injector: Injector, message: string): void {
  import('@angular/material/snack-bar')
    .then(({ MatSnackBar }) => {
      const snackBar = injector.get(MatSnackBar, null);
      if (snackBar) {
        snackBar.open(message, '확인', { duration: 6000 });
      } else {
        console.error(`[Infra Error] ${message}`);
      }
    })
    .catch(() => console.error(`[Infra Error] ${message}`));
}

function dispatch(
  req: HttpRequest<unknown>,
  path: string,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  auth: SpinoffAuthService,
  config: { resolveOrgScopedId?: (path: string) => string | null },
): Observable<HttpEvent<unknown>> {
  // org-scoped 라우트(`resolveOrgScopedId` 매칭 — opt-in한 앱만 설정)는 personal 대신
  // org access token을 발급(캐시)·부착한다(2026-07-23). 설정 안 한 앱(commerce/accounts 등)은
  // 항상 아래 personal 분기로 빠져 기존 동작 그대로 유지된다.
  const orgId = config.resolveOrgScopedId?.(path) ?? null;
  if (orgId) {
    return dispatchOrgScoped(req, orgId, next, auth);
  }
  return dispatchPersonal(req, path, next, auth);
}

function dispatchOrgScoped(
  req: HttpRequest<unknown>,
  orgId: string,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  auth: SpinoffAuthService,
): Observable<HttpEvent<unknown>> {
  // org-scoped 라우트는 org access token으로만 호출한다 — 토큰을 못 받거나(mint 401) 재발급한 토큰으로도 401이면 personal 토큰으로 되돌려 보내지 않고 그 에러를 그대로 던진다
  // (2026-10-06, personal 폴백 제거 — `docs/auth/auth-patterns.md` 「org-scoped 요청의 토큰」). personal 토큰은 로그인·개인 정보 조회 전용이다.
  return attemptOrgToken(req, orgId, next, auth);
}

// org 토큰으로 요청을 시도한다 — 401이면 토큰을 무효화하고 재발급 후 1회만 재시도한다.
// 재발급(mint) 자체가 실패하든, 재발급된 토큰으로 보낸 재요청이 또 401이든 구분하지 않고
// 에러를 그대로 던진다 — 어느 쪽이든 "이 org에 대한 role을 org 토큰으로 확정할 수 없다"는 뜻이다.
function attemptOrgToken(
  req: HttpRequest<unknown>,
  orgId: string,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  auth: SpinoffAuthService,
): Observable<HttpEvent<unknown>> {
  return from(auth.getOrgAccessToken(orgId)).pipe(
    switchMap((token) =>
      next(withBearer(req, token)).pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            auth.invalidateOrgAccessToken(orgId);
            return from(auth.getOrgAccessToken(orgId)).pipe(
              switchMap((freshToken) => next(withBearer(req, freshToken))),
            );
          }
          return throwError(() => err);
        }),
      ),
    ),
  );
}

function dispatchPersonal(
  req: HttpRequest<unknown>,
  path: string,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  auth: SpinoffAuthService,
): Observable<HttpEvent<unknown>> {
  const authed = withBearer(req, auth.accessToken());

  return next(authed).pipe(
    catchError((err) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !isAuthRoute(path)
      ) {
        // 세션을 지울지는 refresh 결과로만 판단한다 — catchError 를 switchMap 앞에 둬서 다시 보낸 원래 요청의 실패(예: refresh 는 됐지만 권한이 없어 403,
        // role 이 없는 org 의 mint 401)는 세션 만료가 아니므로 세션을 지우지 않는다(2026-08-19 발견: role 이 없을 뿐인 정상 사용자의 세션이 로그아웃될 뻔했다, Gemini 리뷰 2026-10-07).
        return from(auth.refresh()).pipe(
          catchError((refreshErr) => {
            if (isSessionRejected(refreshErr)) auth.clearSession();
            return throwError(() => refreshErr);
          }),
          switchMap(() => next(withBearer(req, auth.accessToken()))),
        );
      }
      return throwError(() => err);
    }),
  );
}

/**
 * refresh 실패 중 세션을 지울 것은 서버가 refresh 를 거절했을 때(401·403)뿐이다. 네트워크 단절·5xx 같은 일시 장애에 세션을
 * 지우면 앱이 로그인 화면으로 보내 작성 중이던 입력을 잃는다 — 그런 장애는 `infraError` 로 따로 알린다(Ruderud 리뷰, 2026-10-07).
 */
function isSessionRejected(err: unknown): boolean {
  return err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403);
}

function withBearer(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
}

/** apiBaseUrl을 떼어낸 경로가 항상 `/`로 시작하도록 정규화한다 — apiBaseUrl 설정값 끝에
 * 슬래시가 붙어 있으면(예: `http://api.test/`) 떼어낸 결과의 시작 슬래시까지 함께 없어져
 * `resolveOrgScopedId`/`isAuthRoute`의 `^/...` 패턴 매칭이 조용히 실패한다(Gemini 리뷰). */
function stripBaseUrl(url: string, apiBaseUrl: string): string {
  const stripped = url.replace(apiBaseUrl, '');
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

function isAuthRoute(path: string): boolean {
  // `/auth/sso/*`(학교종이 로그인·단체 연동 콜백)는 의도적으로 이 목록에 없다 —
  // 단체 연동 콜백은 개인 세션 Bearer가 필요해 401 시 갱신을 시도해야 한다.
  return path.startsWith('/auth/signin') ||
    // 단체 소유 계정의 코드 로그인 — 틀린 코드의 401은 "세션이 만료됐다"가 아니라 로그인 실패 자체다. 여기 없으면 인터셉터가 세션 갱신(`/auth/refresh`)을 시도하고 그 실패를 대신
    // 던져 원래 응답(401/429)을 잃는다.
    path.startsWith('/auth/org-managed') ||
    path.startsWith('/auth/signup') ||
    path.startsWith('/auth/social') ||
    path.startsWith('/auth/refresh') ||
    path.startsWith('/auth/signout');
}
